import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TextField, Autocomplete } from "@mui/material";
import debounce from "lodash/debounce";
import { createUseStyles } from "react-jss";
import { theme } from "$utils/theme/bataillog";
import { AddressService } from "$services";

type Props = {
  disabled?: boolean;
  error?: string;
  onChange: (values: any, rows: any) => void;
  required?: boolean;
  value: any;
  useOnInputChange?: boolean;
};

function AddressSelect({
  error,
  onChange,
  value,
  disabled = false,
  required = false,
  useOnInputChange = true,
}: Props) {
  const classes = useStyles();
  const [placeOptions, setPlaceOptions] = useState([]);
  const [filter, setFilter] = useState("");

  const handleSearchInput = useMemo(
    () =>
      debounce((e, val) => {
        setFilter(val);
      }, 500),
    []
  );

  const getSuggestions = useCallback(async (val: string) => {
    try {
      const res = await AddressService.GetLocations(val);

      return res ?? [];
    } catch (e) {
      console.error(e);
    }
    return [];
  }, []);

  useEffect(() => {
    if (filter) {
      getSuggestions(filter).then((responses: any) => {
        setPlaceOptions(responses);
      });
    } else {
      setPlaceOptions([]);
    }
  }, [filter, getSuggestions]);

  //
  return (
    <Autocomplete
      onInputChange={(e, val) => {
        handleSearchInput(e, val);
        if (useOnInputChange) {
          onChange(val, val);
        }
      }}
      disableClearable
      disabled={disabled}
      className={classes.select}
      options={placeOptions}
      onChange={async (event: any, newValues: any) => {
        const detail = await AddressService.GetLocationDetail(
          newValues.place_id
        );
        onChange(detail, detail);
      }}
      value={value}
      getOptionLabel={(option: any) => option?.description || option}
      noOptionsText="Saisir une adresse valide"
      placeholder="Saisir une adresse"
      renderInput={(params: any) => (
        <TextField
          {...params}
          label={`Adresse${required ? " *" : ""}`}
          variant="outlined"
          disabled={disabled}
          style={{ width: "100%" }}
          error={!!error}
        />
      )}
    />
  );
}

const useStyles = createUseStyles(() => ({
  select: {
    flex: "1",
    paddingRight: "20px",
    "&:last-child": {
      padding: 0,
    },
    background: theme.color.white,
    "& .MuiInputLabel-shrink": {
      background: `${theme.color.white} !important`,
    },
  },
}));

export default AddressSelect;
